import { Close } from '@mui/icons-material'
import { Popover, Typography, useTheme } from '@mui/material'
import { t } from 'i18next'
import React, { useCallback } from 'react'
import { toast } from 'react-toastify'
import { invalidateLocalityCache } from 'src/api/localities'
import { invalidateMaintenanceTemplateCache } from 'src/api/maintenanceTemplate'
import { invalidateScheduledMaintenanceCache } from 'src/api/scheduled-maintenance'
import BasicDatePicker from 'src/components/DateTimePicker'
import { AppForm, FormSubmitButton } from 'src/components/FormCore'
import { MainButton } from 'src/components/MainButton'
import { useZodForm } from 'src/hooks/zod-form'
import { MUIThemeColors } from 'src/mui-theme'
import { Flex } from 'src/styles/flexComponent'
import { Padding } from 'src/styles/paddingComponent'
import { getThemeColor } from 'src/utils'
import { getUTCDate } from 'src/utils/function'
import { z } from 'zod'

interface Props {
  anchorEl: HTMLButtonElement | null
  setAnchorEl: React.Dispatch<React.SetStateAction<HTMLButtonElement | null>>
  onSubmitAssignData: (data) => void
  selectedData:
  | {
    localityId: number
    maintenanceTemplateId: number
  }
  | undefined
  customerId: EntityId
}

const LocalityAssignToMaintenanceTemplateForm = ({
  anchorEl,
  onSubmitAssignData,
  setAnchorEl,
  selectedData,
  customerId,
}: Props) => {
  const open = Boolean(anchorEl)
  const theme = useTheme()
  const schema = z
    .object({
      startDate: z
        .date()
        .or(z.string())
        .default(() => getUTCDate(new Date()) || new Date()),
      endDate: z.date().or(z.string()).optional(),
    })
    .refine(data => data.endDate == null || data.endDate > data.startDate, {
      message: t('endStartDateError', { ns: 'common' }),
      path: ['endDate'],
    })

  const form = useZodForm(schema)

  const onSubmit = useCallback(
    async (data: any) => {
      await onSubmitAssignData({
        startDate: data.startDate,
        endDate: data.endDate,
        localityId: selectedData?.localityId,
        maintenanceTemplateId: selectedData?.maintenanceTemplateId,
        isChecked: true,
      })
      invalidateMaintenanceTemplateCache.getLocalityMaintenanceTemplates(customerId as EntityId)
      invalidateScheduledMaintenanceCache.getLocalityScheduledMaintenances(
        customerId as EntityId,
        selectedData?.localityId as EntityId,
      )
      invalidateLocalityCache.getLocality(
        customerId as EntityId,
        selectedData?.localityId as EntityId,
      )
      toast.success(t('assign-success', { ns: 'maintenance' }))

      setAnchorEl(null)
      form.reset()
    },
    [form, selectedData, customerId],
  )

  return (
    <PopOverActiveLocality
      open={open}
      anchorEl={anchorEl}
      onClose={() => {
        form.reset()
        setAnchorEl(null)
      }}
    >
      <Padding.p14 width={270}>
        <Flex.Row justifyContent={'space-between'} paddingLeft={'8px'}>
          <Typography variant="body2" sx={{ color: getThemeColor(theme, MUIThemeColors.primaryMain) }}>
            {t('assign-to-locality', { ns: 'maintenance' })}
          </Typography>
          <Close
            sx={{ cursor: 'pointer' }}
            onClick={() => {
              form.reset()
              setAnchorEl(null)
            }}
          />
        </Flex.Row>
        <AppForm form={form} onSubmit={onSubmit}>
          <BasicDatePicker name={'startDate'} label={t('startDate', { ns: 'common' })} defaultValue={getUTCDate(new Date())} />
          <BasicDatePicker name={'endDate'} label={t('endDate', { ns: 'common' })} />
          <Flex.Row justifyContent={'end'} alignItems={'center'} paddingTop={'4px'} paddingRight={'8px'}>
            <Padding.pr8>
              <MainButton
                sx={{ width: '84px' }}
                variant="outlined"
                color="primary"
                onClick={() => {
                  form.reset()
                  setAnchorEl(null)
                }}
              >
                {t('cancel', { ns: 'common' })}
              </MainButton>
            </Padding.pr8>
            <FormSubmitButton sx={{ width: '84px' }} createText={t('assign', { ns: 'common' })} />
          </Flex.Row>
        </AppForm>
      </Padding.p14>
    </PopOverActiveLocality>
  )
}

export default LocalityAssignToMaintenanceTemplateForm

export const PopOverActiveLocality = ({ children, open, anchorEl, onClose }) => {
  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      anchorOrigin={{
        vertical: 'top',
        horizontal: 'left',
      }}
      onClose={onClose}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'left',
      }}
    >
      {children}
    </Popover>
  )
}
